import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useMoney } from '../hooks/useMoney';
import {
  Insight,
  InsightSeverity,
  InsightSummary,
  dismissInsight,
  loadInsights,
  restoreInsight,
} from '../services/InsightsService';
import Icon from './Icon';

/**
 * Things the app has noticed — a budget line paid more than it records, a
 * category tagged to no event, a favour box running over its target.
 *
 * The point is that nobody should need to go looking. These are all findings
 * that previously turned up only because someone happened to reconcile the
 * numbers by hand, by which time the money had usually already moved.
 *
 * Deliberately quiet when there is nothing wrong: an empty state that says
 * "nothing to flag" is a real result and worth showing, but it stays small.
 */

const SEVERITY_BADGE: Record<InsightSeverity, string> = {
  critical: 'ds-badge-danger',
  warning: 'ds-badge-warning',
  info: 'ds-badge-info',
};

const SEVERITY_LABEL: Record<InsightSeverity, string> = {
  critical: 'Needs attention',
  warning: 'Worth checking',
  info: 'For information',
};

const SEVERITY_ICON: Record<InsightSeverity, string> = {
  critical: 'alert',
  warning: 'warning',
  info: 'helpCircle',
};

interface InsightsPanelProps {
  /** Cap the list; the rest stay behind "show all". Omit for no cap. */
  limit?: number;
  /** Rendered inside a sidebar column rather than as a full-width panel. */
  compact?: boolean;
  onNavigate?: (href: string) => void;
  /** Restrict to insights whose `rule` starts with one of these prefixes
   *  (e.g. ['budget.', 'fx.'] on the Budget page) — insights are tagged by
   *  domain ('task.overdue', 'bundle.over-target', …), and the full,
   *  unfiltered set only belongs on the Dashboard. Omit for no filter. */
  rulePrefixes?: string[];
  /** Reports how many insights are on screen, so a host (the notification
   *  dropdown) can decide whether its own "nothing here" state applies. */
  onCountChange?: (count: number) => void;
}

export interface InsightsPanelHandle {
  /** Dismisses every currently non-dismissed insight — used by the
   *  notification dropdown's "Mark all read" so insights don't keep
   *  showing after the rest of the list has been cleared. */
  dismissAll: () => Promise<void>;
}

export const InsightsPanel = React.forwardRef<InsightsPanelHandle, InsightsPanelProps>(({
  limit,
  compact = false,
  onNavigate,
  rulePrefixes,
  onCountChange,
}, ref) => {
  const money = useMoney();
  const [summary, setSummary] = useState<InsightSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = useCallback(async (withDismissed: boolean) => {
    setLoading(true);
    const next = await loadInsights(withDismissed);
    setSummary(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh(showDismissed);
  }, [refresh, showDismissed]);

  useImperativeHandle(ref, () => ({
    dismissAll: async () => {
      const current = (await loadInsights(false)).insights.filter(i => !i.dismissed);
      await Promise.all(current.map(i => dismissInsight(i.key)));
      await refresh(showDismissed);
    },
  }), [refresh, showDismissed]);

  // Derived here rather than from the `insights` const below, because that
  // is computed after an early return — a hook placed down there is called
  // conditionally, which is exactly the crash this file shipped once already.
  const prefixKey = rulePrefixes?.join('|') ?? '';
  useEffect(() => {
    const all = summary?.insights ?? [];
    const prefixes = prefixKey ? prefixKey.split('|') : null;
    const n = prefixes ? all.filter(i => prefixes.some(p => i.rule.startsWith(p))).length : all.length;
    onCountChange?.(n);
  }, [summary, prefixKey, onCountChange]);

  const handleDismiss = async (insight: Insight) => {
    setBusyKey(insight.key);
    const ok = insight.dismissed
      ? await restoreInsight(insight.key)
      : await dismissInsight(insight.key);
    // Refetch rather than patching local state: dismissing changes the counts
    // and the at-stake total, and recomputing server-side is the only version
    // of those numbers that is guaranteed to agree with everything else.
    if (ok) await refresh(showDismissed);
    setBusyKey(null);
  };

  const go = (href?: string) => {
    if (!href) return;
    if (onNavigate) onNavigate(href);
    else window.location.hash = href;
  };

  if (loading && !summary) {
    return (
      <section className="insights-panel">
        <p className="insights-empty">Checking for anything that needs attention…</p>
      </section>
    );
  }

  const allInsights = summary?.insights ?? [];
  const insights = rulePrefixes
    ? allInsights.filter(i => rulePrefixes.some(p => i.rule.startsWith(p)))
    : allInsights;
  // counts/atStakeNaira come pre-aggregated across every insight, so a
  // rule-filtered view can't reuse them — recompute from the filtered list
  // (still only over non-dismissed findings, matching the server's own rule).
  const counts = rulePrefixes
    ? insights.reduce((acc, i) => {
        if (!i.dismissed) acc[i.severity] += 1;
        return acc;
      }, { critical: 0, warning: 0, info: 0 })
    : summary?.counts ?? { critical: 0, warning: 0, info: 0 };
  // The API has always returned this and the panel has always discarded it.
  // It is the one number that says how much the findings are actually worth
  // — counts alone cannot distinguish three typos from three missing payments.
  const atStake = rulePrefixes
    ? insights.reduce((sum, i) => sum + (!i.dismissed ? i.amountNaira ?? 0 : 0), 0)
    : summary?.atStakeNaira ?? 0;
  const visible = showAll || !limit ? insights : insights.slice(0, limit);
  const hidden = insights.length - visible.length;

  // Compact mode (the notification bell dropdown) renders each insight as a
  // plain row — icon, title/detail, dismiss — matching the regular
  // notification list it sits above, instead of the full card layout with
  // its own badges/actions/footer per item, which read as a second,
  // unrelated widget bolted onto the dropdown rather than part of the list.
  if (compact) {
    return (
      <section className="insights-panel insights-panel-compact">
        {insights.length === 0 ? (
          // No message here even once loaded — compact mode only ever runs
          // inside the notification dropdown, which already shows its own
          // single "nothing here" empty state once both notifications and
          // insights are empty. Rendering one here too just stacked two
          // near-identical messages on top of each other.
          null
        ) : (
          <ul className="insight-row-list">
            {visible.map((i) => (
              <li
                key={i.key}
                className={`insight-row${i.dismissed ? ' insight-dismissed' : ''}`}
                onClick={() => go(i.href)}
              >
                <div className="insight-row-icon">
                  <div className={`insight-row-icon-tone insight-row-icon-tone--${i.severity}`}>
                    <Icon name={SEVERITY_ICON[i.severity]} size={16} />
                  </div>
                </div>
                <div className="insight-row-content">
                  <p className="insight-row-message">{i.title}</p>
                  <span className="insight-row-detail">{i.detail}</span>
                </div>
                <button
                  type="button"
                  className="insight-row-dismiss"
                  disabled={busyKey === i.key}
                  onClick={(e) => { e.stopPropagation(); void handleDismiss(i); }}
                  title={i.dismissed ? 'Restore' : 'Dismiss'}
                >
                  <Icon name={i.dismissed ? 'refresh' : 'x'} size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {hidden > 0 && (
          <button
            type="button"
            className="ds-btn-ghost insights-more"
            onClick={() => setShowAll(true)}
          >
            Show {hidden} more
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="insights-panel">
      <header className="insights-header">
        <div className="insights-title">
          <Icon name="lightning" size={16} />
          <h3>What needs attention</h3>
        </div>
        <div className="insights-summary">
          {atStake > 0 && (
            <span className="insights-at-stake" title="Total of the findings that carry an amount">
              {money.format(atStake)} at stake
            </span>
          )}
          {counts.critical > 0 && (
            <span className="ds-badge ds-badge-danger">{counts.critical} urgent</span>
          )}
          {counts.warning > 0 && (
            <span className="ds-badge ds-badge-warning">{counts.warning} to check</span>
          )}
          {counts.info > 0 && (
            <span className="ds-badge ds-badge-info">{counts.info} FYI</span>
          )}
          <button
            type="button"
            className="ds-btn-ghost insights-toggle"
            onClick={() => setShowDismissed((v) => !v)}
          >
            {showDismissed ? 'Hide dismissed' : 'Show dismissed'}
          </button>
        </div>
      </header>

      {insights.length === 0 ? (
        <p className="insights-empty">
          Nothing to flag. No budget line is paid beyond what it records, every category is
          tagged to an event, and no bundle is over its target.
        </p>
      ) : (
        <ul className="insights-list">
          {visible.map((i) => (
            <li
              key={i.key}
              className={`insight-card insight-${i.severity}${i.dismissed ? ' insight-dismissed' : ''}`}
            >
              <div className="insight-icon">
                <Icon name={SEVERITY_ICON[i.severity]} size={16} />
              </div>
              <div className="insight-body">
                <div className="insight-top">
                  <h4 className="insight-title">{i.title}</h4>
                  <span className={`ds-badge ${SEVERITY_BADGE[i.severity]}`}>
                    {SEVERITY_LABEL[i.severity]}
                  </span>
                </div>
                <p className="insight-detail">{i.detail}</p>
                {i.action && (
                  <p className="insight-action">
                    <Icon name="arrowRight" size={13} />
                    <span>{i.action}</span>
                  </p>
                )}
                <div className="insight-footer">
                  {i.href && (
                    <button type="button" className="ds-btn-ghost" onClick={() => go(i.href)}>
                      Open
                    </button>
                  )}
                  <button
                    type="button"
                    className="ds-btn-ghost"
                    disabled={busyKey === i.key}
                    onClick={() => void handleDismiss(i)}
                  >
                    {i.dismissed ? 'Restore' : 'Dismiss'}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hidden > 0 && (
        <button
          type="button"
          className="ds-btn-ghost insights-more"
          onClick={() => setShowAll(true)}
        >
          Show {hidden} more
        </button>
      )}
    </section>
  );
});

InsightsPanel.displayName = 'InsightsPanel';

export default InsightsPanel;
